"use client";

import { useMemo } from "react";
import { useTranslations } from "@zapier/i18n";
import { Avatar, Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useAccounts } from "@zapier/client";
import { useCurrentAccountId } from "@zapier/identity";

import styles from "./AccountPickerContent.module.css";

interface Props {
  selectedAccountId: string | null;
  /**
   * Called with the new selection. Clicking an already-selected row deselects
   * it (passes `null` for `accountId` and `""` for `accountName`), matching
   * folder picker toggle behavior.
   */
  onSelect: (accountId: string | null, accountName: string) => void;
}

/**
 * Body-only content for the "Select a destination account" step.
 *
 * List-style picker (avatars + name + account-type subtitle + selection
 * check-mark) replacing the old `<Select>` dropdown. Structurally based on
 * a "Workspace Picker" layout but uses AM's "account"
 * terminology throughout.
 *
 * The current account appears in the list but is disabled — it's where the
 * user is right now, not a valid destination.
 */
export function AccountPickerContent({ selectedAccountId, onSelect }: Props) {
  const t = useTranslations("copy-to-another-account-modal");
  const currentAccountId = useCurrentAccountId();
  const { accounts } = useAccounts();

  const rows = useMemo(() => {
    // HQ/org accounts are not valid copy destinations: the asset would land
    // OFF and the user can't access it (they get a 403). Hide them from the
    // picker. See ASM-4404.
    const mapped = accounts
      .filter((account) => !account.isHqAccount)
      .map((account) => ({
        id: String(account.id),
        name: account.name ?? "",
        logoUrl: account.logoUrl ?? undefined,
        isCurrent: Number(account.id) === Number(currentAccountId),
        // `planInfo.allowTeams` is only true for team-plan accounts; everyone
        // else is a personal account.
        typeLabel: account.planInfo?.allowTeams
          ? t("account-step.type-team")
          : t("account-step.type-personal"),
      }));
    // The current (disabled) account renders last so the
    // selectable destinations appear at the top of the list.
    return [...mapped].sort((a, b) => {
      if (a.isCurrent === b.isCurrent) return 0;
      return a.isCurrent ? 1 : -1;
    });
  }, [accounts, currentAccountId, t]);

  const hasOtherAccount = rows.some((r) => !r.isCurrent);

  if (!hasOtherAccount) {
    return (
      <div className={styles.empty}>
        <Text type="Body2" tag="p" color="TextWeaker">
          {t("account-step.empty")}
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.list}>
      {rows.map((row) => {
        const isSelected = selectedAccountId === row.id;
        const displayName = row.isCurrent
          ? `${row.name} ${t("account-step.current-account-suffix")}`
          : row.name;
        const rowClass = row.isCurrent
          ? styles.rowDisabled
          : isSelected
            ? styles.rowSelected
            : styles.row;

        return (
          <button
            key={row.id}
            type="button"
            className={rowClass}
            onClick={
              row.isCurrent
                ? undefined
                : // Toggle: clicking the already-selected row deselects.
                  () =>
                    isSelected
                      ? // eslint-disable-next-line unicorn/no-null
                        onSelect(null, "")
                      : onSelect(row.id, row.name)
            }
            disabled={row.isCurrent}
          >
            <Avatar
              url={row.logoUrl}
              name={row.name}
              size="medium"
              isInteractive={!row.isCurrent}
            />
            <div className={styles.info}>
              <Text
                type="Body3"
                tag="span"
                color={isSelected ? "TextLink" : undefined}
                className={isSelected ? styles.nameSelected : styles.name}
              >
                {displayName}
              </Text>
              <Text
                type="SmallPrint1"
                tag="span"
                color={isSelected ? "TextLink" : "TextWeaker"}
              >
                {row.typeLabel}
              </Text>
            </div>
            {isSelected ? (
              <span aria-label="selected" className={styles.checkmark}>
                <Icon name="formCheck" size="1.25em" />
              </span>
            ) : undefined}
          </button>
        );
      })}
    </div>
  );
}
